import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, SunDim } from "lucide-react";
import { z } from "zod";
import { resetPasswordApiV1LoginResetPasswordPost } from "@/client";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const apiDocsUrl = import.meta.env.VITE_API_URL + "/docs";

const resetPasswordSchema = z.object({
  token: z.string(),
  password: z
    .string()
    .min(8, {
      message: "Password must be at least 8 characters.",
    })
    .max(40, {
      message: "Password must be at most 40 characters.",
    }),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: z.object({
    token: z.string(),
  }),
  beforeLoad: ({ search }) => {
    if (!search.token) {
      throw redirect({ to: "/login" });
    }
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token,
      password: "",
    },
  });

  const { mutateAsync: resetPassword, error } = useMutation({
    mutationFn: resetPasswordApiV1LoginResetPasswordPost,
  });

  async function onSubmit(values: z.infer<typeof resetPasswordSchema>) {
    await resetPassword({
      requestBody: {
        token: values.token,
        new_password: values.password,
      },
    });
    navigate({ to: "/reset-password-success" });
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between p-8">
      <section className="text-center">
        <a href={apiDocsUrl} className="text-sm text-muted-foreground" target="_blank" rel="noopener noreferrer">
          {t("Visit the OPENAPI documentation")}: <span className="underline">{apiDocsUrl}</span>
        </a>
      </section>
      <section className="flex flex-col items-center justify-center gap-2 shadow-lg">
        <h1 className="text-2xl font-semibold tracking-tight">{t("Reset API Password")}</h1>
        <p className="text-sm text-muted-foreground">{t("Enter your new password below")}</p>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex h-full w-full min-w-[300px] max-w-sm flex-col gap-4 rounded-lg p-4 md:min-w-[500px]"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("New Password")}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? <Loader2 className="animate-spin" /> : t("Reset Password")}
            </Button>
            {error && <p className="text-center text-red-500">{t(error.message)}</p>}
          </form>
        </Form>
      </section>
      <div className="flex flex-col items-center gap-2 p-4">
        <SunDim className="h-16 w-16 text-primary" />
        <h2 className="text-balance text-center text-2xl font-bold text-muted-foreground">
          Brainchild Commodity Intelligence
        </h2>
      </div>
    </div>
  );
}
